/**
 * FloodRiskPanel — Lam Takhong dam live status (เขื่อนลำตะคอง).
 *
 * Real-time daily accounting from the ThaiWater analyst feed: storage vs
 * normal high (314.49 MCM) and max retention (445 MCM), usable water,
 * inflow/release in MCM/day. Alert bands run both directions — Sikhio's
 * dominant hazard is drought (low storage), with monsoon flood as the
 * high-side risk.
 */

import type { DamReading, FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";
import { DAM_COLOR, DAM_LABEL_TH } from "../lib/waterSecurity";

interface Props {
  data: DamReading | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div style={{ height: 5, background: "var(--line)", position: "relative", marginTop: 4 }}>
      <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
      {/* 30 % marker — RID's public "restriction" threshold */}
      <div style={{ position: "absolute", left: "30%", top: -2, height: 9, width: 1, background: "var(--text-3)" }} />
    </div>
  );
}

export function FloodRiskPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  const r = data;

  if (loading && !r) {
    return (
      <div className="col">
        <div className="eyebrow">เขื่อน / LAM TAKHONG</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "70%" }} />
      </div>
    );
  }

  if (!r) {
    return (
      <div className="col" style={{ gap: 4 }}>
        <PanelHeader
          title="เขื่อน "
          fallbackTier={fallbackTier}
          ageMinutes={ageMinutes}
          source="thaiwater.rid"
        />
        <div className="caption mono" style={{ color: "var(--warn)" }} role="status">
          ◆ FEED GAP · Lam Takhong telemetry unavailable
        </div>
      </div>
    );
  }

  const col = DAM_COLOR[r.alertLevel] ?? "var(--data)";
  const alertLbl = DAM_LABEL_TH[r.alertLevel] ?? r.alertLevel;
  const netMcmDay =
    r.inflowMcmDay !== null && r.releasedMcmDay !== null
      ? r.inflowMcmDay - r.releasedMcmDay - (r.evapMcmDay ?? 0)
      : null;

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="เขื่อน "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="thaiwater.rid"
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="mono" style={{ fontSize: "var(--size-h3)", color: col, fontWeight: 700 }}>
          {r.pctNormal.toFixed(1)}%
        </span>
        <span className="eyebrow" style={{ color: col }}>
          {alertLbl}
        </span>
      </div>
      <Bar pct={r.pctNormal} color={col} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: 6 }}>
        <span className="caption">ปริมาณน้ำ</span>
        <span className="mono caption" style={{ textAlign: "right" }}>
          {r.storageMcm.toFixed(1)} / {r.normalStorageMcm.toFixed(0)} ล้าน ม.³
        </span>
        {r.usableMcm != null && (
          <>
            <span className="caption">น้ำใช้การได้</span>
            <span className="mono caption" style={{ textAlign: "right" }}>
              {r.usableMcm.toFixed(1)} ล้าน ม.³{r.usablePct != null ? ` (${r.usablePct.toFixed(0)}%)` : ""}
            </span>
          </>
        )}
        {r.inflowMcmDay != null && (
          <>
            <span className="caption">น้ำไหลเข้า</span>
            <span className="mono caption" style={{ textAlign: "right" }}>{r.inflowMcmDay.toFixed(2)} ล้าน ม.³/วัน</span>
          </>
        )}
        {r.releasedMcmDay != null && (
          <>
            <span className="caption">ระบายออก</span>
            <span className="mono caption" style={{ textAlign: "right" }}>{r.releasedMcmDay.toFixed(2)} ล้าน ม.³/วัน</span>
          </>
        )}
        {netMcmDay != null && (
          <>
            <span className="caption">สมดุลสุทธิ</span>
            <span
              className="mono caption"
              style={{ textAlign: "right", color: netMcmDay < 0 ? "var(--warn)" : "var(--good)" }}
            >
              {netMcmDay >= 0 ? "+" : ""}{netMcmDay.toFixed(2)} ล้าน ม.³/วัน
            </span>
          </>
        )}
      </div>
      <div className="caption" style={{ marginTop: 4, color: "var(--text-3)" }}>
        ข้อมูลวันที่ {r.date} · {r.agency} · ความจุสูงสุด {r.maxStorageMcm.toFixed(0)} ล้าน ม.³
      </div>
    </div>
  );
}
